<template>
  <v-card>
    <v-form @submit.prevent="submitEditRcti()" ref="editRctiForm">
      <v-container>
        <v-layout row wrap>
          <dialog-header
            :title="'Edit'"
            :details="`Rcti (${rcti.status})`"
            :time="editedRcti.updatedAt"
          ></dialog-header>
          <flex-sub-header>RCTI Header Details</flex-sub-header>
          <v-flex xs12 sm6>
            <v-text-field
              :disabled="closed"
              label="Reference No."
              v-model="editedRcti.externalRef"
              prepend-icon="insert_drive_file"
              type="text"
            ></v-text-field>
          </v-flex>
          <v-flex xs12 sm6>
            <v-select
              label="Status"
              v-model="editedRcti.status"
              prepend-icon="info"
              :items="statusItems"
              type="text"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm6>
            <v-select
              :disabled="closed"
              label="Billing Period"
              v-model="editedRcti.billingPeriod"
              prepend-icon="calendar_today"
              :items="allBillingPeriods"
              type="text"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm6>
            <date-picker-wrapper
              :disabled="closed"
              v-model="editedRcti.date"
              label="Date"
              color="indigo"
            ></date-picker-wrapper>
          </v-flex>

          <flex-sub-header>Stat Dec</flex-sub-header>
          <v-flex xs12 sm6 class="flex-container-space-between">
            <v-select
              :disabled="closed"
              label="Stat Dec Status"
              :items="statDecStatusItems"
              v-model="editedRcti.statDecConfirmed"
              prepend-icon="file_copy"
            ></v-select>
          </v-flex>
          <v-flex xs12 sm6 class="flex-container-space-between">
            <v-text-field
              :disabled="closed"
              prepend-icon="note"
              v-model="editedRcti.statDecNotes"
              label="Stat Dec Notes"
              type="text"
            ></v-text-field>
          </v-flex>
          <v-flex xs12>
            <v-textarea
              :disabled="closed"
              rows="1"
              auto-grow
              :rules="internalNotesRules"
              label="Internal Notes"
              v-model="editedRcti.internalNotes"
              prepend-icon="note"
              type="text"
            ></v-textarea>
          </v-flex>
          <v-flex xs12>
            <v-textarea
              :disabled="closed"
              rows="1"
              auto-grow
              :rules="invoiceNotesRules"
              label="Invoice Notes"
              v-model="editedRcti.invoiceNotes"
              prepend-icon="note"
              type="text"
            ></v-textarea>
          </v-flex>

          <flex-sub-header>RCTI Line Items</flex-sub-header>
          <v-layout v-for="(line, i) of editedRcti.rctiLines" :key="i">
            <v-flex xs12 sm9>
              <v-text-field
                :disabled="closed"
                label="Description"
                v-model="line.description"
                prepend-icon="note"
                type="text"
              ></v-text-field>
            </v-flex>
            <v-flex xs12 sm3>
              <v-text-field
                :disabled="closed"
                label="Line Total"
                v-model.number="line.costTotal"
                prepend-icon="attach_money"
                type="text"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-container>
      <v-container fluid>
        <v-layout>
          <v-flex class="text-xs-right" v-if="!!rctiTotal">
            <span class="subheading grey--text text--darken-2">Total: ${{rctiTotal.toFixed(2)}}</span>
          </v-flex>
        </v-layout>
      </v-container>
      <v-card-actions>
        <v-spacer></v-spacer>

        <div>
          <v-btn color="grey darken-2" flat @click="discard()">Discard?</v-btn>
          <v-btn color="green" flat type="submit" :loading="loading">Save</v-btn>
        </div>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import Store from '@/store/computed/storeHelpers'
import RctiService from '@/services/requests/rctiService'

// MIXINS
import RctiValidation from '@/services/validation/rctiValidation'
import FormatDates from '@/services/mixins/formatDates'
import BillingPeriods from '@/services/mixins/billingPeriods'

// COMPONENTS
import DialogHeader from '@/components/ui/dialogHeader'
import DatePickerWrapper from '@/components/ui/datePickerWrapper'

export default {
  mixins: [RctiValidation, FormatDates, BillingPeriods],
  components: {
    DialogHeader,
    DatePickerWrapper,
  },
  data() {
    return {
      editedRcti: this.cloneRcti(this.rcti),
      loading: false,
      statusItems: ['ENTERED', 'REVIEW', 'APPROVED', 'SENT', 'CLOSED'],
      statDecStatusItems: ['CONFIRMED', 'REVIEW', 'NOT SET'],
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    discard() {
      this.editedRcti = this.cloneRcti(this.rcti)
      this.close()
    },
    submitEditRcti() {
      if (!this.$refs.editRctiForm.validate()) {
        this.snack({ text: 'Please correct form errors', color: 'red' })
        return
      }
      this.save()
    },
    save() {
      this.loading = true
      RctiService.updateRcti(this.editedRcti)
        .then((result) => {
          //console.log(result.data.result);
          this.loading = false
          result.data.result.rcti.updated = true
          this.$store.commit('rctis/updateArrayStateByValue', {
            prop: 'rctis',
            value: this.rcti,
            editedValue: result.data.result.rcti,
          })
          this.editedRcti = this.cloneRcti(result.data.result.rcti)
          this.snack({ text: 'Rcti was edited!', color: 'green' })
          this.close()
        })
        .catch((err) => {
          this.loading = false
          console.log(err)
          this.snackError()
        })
    },
    calculateTotal(array, prop) {
      return this.$worker.run(
        (array, prop) => {
          var total = 0
          for (var row of array) {
            if (!!row[prop]) {
              var number = Number(row[prop])
              total += number
            }
          }
          return total
        },
        [array, prop]
      )
    },
    cloneRcti(rcti) {
      var editedRcti = Object.assign({}, rcti)
      editedRcti.date = this.makeComputerDate(editedRcti.date)
      if (editedRcti.statDecConfirmed == 'UNCONFIRMED') {
        editedRcti.statDecConfirmed = 'NOT SET'
      }
      return editedRcti
    },
  },
  computed: {
    different() {
      if (!this._.isEqual(this.rcti, this.editedRcti)) {
        return true
      } else {
        return false
      }
    },
  },
  asyncComputed: {
    async rctiTotal() {
      return await this.calculateTotal(this.editedRcti.rctiLines, 'costTotal')
    },
  },
  props: {
    rcti: Object,
    closed: Boolean,
  },
}
</script>

<style>
</style>
